import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  LockOutlined,
  SmileOutlined,
  SolutionOutlined
} from "@ant-design/icons"
import { Flex, Typography } from "antd"
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import { type CustomerPublishState } from "./education-track-helpers.ts"
import type { Audience } from "./types/education.types.ts"

const { Text, Title } = Typography

const AUDIENCE_ICON: Record<Audience, React.ReactNode> = {
  sales_person: <SolutionOutlined aria-hidden className={"text-xl"} />,
  customer: <SmileOutlined aria-hidden className={"text-xl"} />
}

const PUBLISH_ICON: Record<CustomerPublishState, React.ReactNode> = {
  published: <CheckCircleOutlined aria-hidden />,
  reviewRequired: <ExclamationCircleOutlined aria-hidden />,
  unpublished: <ClockCircleOutlined aria-hidden />
}

const copyKey = (audience: Audience) =>
  audience === "customer" ? "customer" : "sales"
const titleKey = (audience: Audience) =>
  audience === "customer" ? "customer" : "salesPerson"

export interface AudienceTrackOption {
  audience: Audience
  /** Only meaningful for the customer option — the sales card never shows a
   *  publish state. */
  publishState?: CustomerPublishState
}

interface AudienceTrackSelectorProps {
  options: AudienceTrackOption[]
  activeAudience: Audience
  onSelect: (audience: Audience) => void
}

export const audienceTabId = (audience: Audience) =>
  `education-track-tab-${audience}`
export const audienceTabPanelId = (audience: Audience) =>
  `education-track-tabpanel-${audience}`

/**
 * Screen 2's audience switch. Replaces a plain AntD `Segmented` after the
 * product owner couldn't tell the sales pitch from the customer guide apart
 * live ("I cannot see the split") — two large, unmistakably different
 * cards, each naming who reads it and whether it's private or
 * customer-facing, real `tablist`/`tab` semantics underneath (arrow
 * keys move both focus and selection, matching the WAI-ARIA tabs pattern)
 * so it stays one control, not two independent buttons.
 */
export function AudienceTrackSelector({
  options,
  activeAudience,
  onSelect
}: AudienceTrackSelectorProps) {
  const { t } = useTranslation()
  const tabRefs = useRef<Partial<Record<Audience, HTMLButtonElement | null>>>(
    {}
  )

  const activeIndex = options.findIndex(
    (option) => option.audience === activeAudience
  )

  const focusAndSelect = (index: number) => {
    const option = options[(index + options.length) % options.length]
    onSelect(option.audience)
    tabRefs.current[option.audience]?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault()
      focusAndSelect(activeIndex + 1)
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault()
      focusAndSelect(activeIndex - 1)
    } else if (event.key === "Home") {
      event.preventDefault()
      focusAndSelect(0)
    } else if (event.key === "End") {
      event.preventDefault()
      focusAndSelect(options.length - 1)
    }
  }

  return (
    <div
      role={"tablist"}
      aria-label={t("projectEducation.trackSelector.tablistLabel")}
      onKeyDown={handleKeyDown}
      className={
        "sticky top-0 z-10 mb-4 grid grid-cols-1 gap-3 bg-background sm:grid-cols-2"
      }
    >
      {options.map((option) => {
        const isActive = option.audience === activeAudience
        const key = copyKey(option.audience)

        return (
          <button
            key={option.audience}
            ref={(node) => {
              tabRefs.current[option.audience] = node
            }}
            type={"button"}
            role={"tab"}
            id={audienceTabId(option.audience)}
            aria-selected={isActive}
            aria-controls={
              isActive ? audienceTabPanelId(option.audience) : undefined
            }
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(option.audience)}
            data-testid={`audience-track-card-${option.audience}`}
            className={cn(
              "flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-left transition-colors",
              isActive
                ? "border-primary bg-primary-bg"
                : "border-border bg-transparent hover:border-primary"
            )}
          >
            <Flex align={"center"} gap={8}>
              {AUDIENCE_ICON[option.audience]}
              <Title level={5} className={"!m-0"}>
                {t(
                  `projectEducation.tracks.${titleKey(option.audience)}.title`
                )}
              </Title>
            </Flex>

            <Text type={"secondary"} className={"text-sm"}>
              {t(`projectEducation.trackSelector.${key}.subtitle`)}
            </Text>

            <Flex align={"center"} gap={6} className={"mt-1"}>
              {option.audience === "customer" ? (
                <GlobalOutlined aria-hidden />
              ) : (
                <LockOutlined aria-hidden />
              )}
              <Text className={"text-xs"}>
                {t(`projectEducation.trackSelector.${key}.privacy`)}
              </Text>
            </Flex>

            {option.publishState && (
              <Flex
                align={"center"}
                gap={6}
                data-testid={"audience-card-publish-status"}
              >
                {PUBLISH_ICON[option.publishState]}
                <Text
                  type={
                    option.publishState === "reviewRequired"
                      ? "warning"
                      : "secondary"
                  }
                  className={"text-xs"}
                >
                  {t(
                    `projectEducation.trackView.publishStatus.${option.publishState}`
                  )}
                </Text>
              </Flex>
            )}
          </button>
        )
      })}
    </div>
  )
}
